import React from 'react';

/**
 * "Your usual package changed" — the PAR-transition prompt.
 *
 * Shown at the natural point of use only: the first count of an item after a
 * newly verified purchase changed its default package, or the moment the user
 * explicitly changes the count format. Never a settings-review chore, never
 * repeated for a transition the user already answered
 * (par_package_transitions). Exact whole-package equivalents never get here —
 * they auto-resolve with a display refresh.
 *
 * Both choices are stated in operational language ("stock level", "number of
 * cases") — the word "rebase" never reaches the user. Keeping the stock level
 * is the default and house recommendation; using the same NUMBER of new
 * packages moves the normalized stocking target, so that choice always takes
 * an explicit tap here, whatever any suggestion says.
 */

const C = {
  ink: '#3D2518',
  body: '#5A4A42',
  muted: '#8B7B73',
  border: '#C8B5A0',
  panel: '#FFFFFF',
  wash: '#F8F4EE',
  accent: '#B08968',
};

const round2 = (n) => Math.round(n * 100) / 100;

const plural = (word, n) => {
  const w = String(word || '').trim();
  if (!w) return w;
  return round2(n) === 1 || w.endsWith('s') ? w : `${w}s`;
};

const qty = (value, unit) => {
  if (value == null) return '—';
  return `${round2(value)} ${plural(unit || 'unit', value)}`;
};

// "US Foods case" → "cases": the bare package noun for the choice-B title.
const packageNoun = (label) => {
  const words = String(label || '').trim().split(/\s+/);
  const last = words[words.length - 1];
  return last ? plural(last, 2) : 'new packages';
};

export default function ParTransitionPrompt({
  itemName,
  transition,
  recommendation = null,
  busy = false,
  onKeep,
  onPreservePackageCount,
  onDismiss,
}) {
  if (!transition) return null;
  const unit = transition.countUnit || 'unit';
  const newLabel = transition.newPackageLabel || 'new package';
  const delta = transition.changeAmount || 0;
  const suggestsPreserve = recommendation?.recommendation === 'preserve_package_count';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Your usual package changed"
      data-testid="par-transition-prompt"
      style={{
        position: 'fixed', inset: 0, zIndex: 4000,
        background: 'rgba(61, 37, 24, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onDismiss?.(); }}
    >
      <div style={{
        background: C.panel, borderRadius: 14, border: `1px solid ${C.border}`,
        maxWidth: 420, width: '100%', padding: '18px 18px 14px',
        boxShadow: '0 18px 40px rgba(61,37,24,0.25)', fontFamily: 'inherit',
      }}>
        {itemName && (
          <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted }}>
            {itemName}
          </div>
        )}
        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: C.ink, margin: '2px 0 10px' }}>
          Your usual package changed
        </div>

        <div style={{ background: C.wash, borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>
            Current PAR
          </div>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: C.ink }} data-testid="par-transition-current">
            {qty(transition.normalizedPar, unit)}
          </div>
          {transition.parDisplayValue != null && transition.oldPackageLabel && (
            <div style={{ fontSize: '0.72rem', color: C.body }}>
              Originally set as {transition.parDisplayValue} {plural(transition.oldPackageLabel, transition.parDisplayValue)}
            </div>
          )}
        </div>

        {recommendation?.reason && (
          <div style={{
            border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px',
            marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start',
          }} data-testid="par-transition-suggestion">
            <span aria-hidden="true" style={{ color: C.accent, fontWeight: 900 }}>✦</span>
            <div>
              <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>
                Countertop suggestion
              </div>
              <div style={{ fontSize: '0.78rem', color: C.body, lineHeight: 1.4 }}>
                {recommendation.reason}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            disabled={busy}
            data-testid="par-transition-keep"
            onClick={() => onKeep?.()}
            style={{
              border: `2px solid ${C.accent}`, background: C.wash, color: C.ink,
              borderRadius: 10, padding: '10px 12px', fontWeight: 800, fontSize: '0.85rem',
              cursor: busy ? 'wait' : 'pointer', textAlign: 'left',
            }}
          >
            Keep the same stock level
            {!suggestsPreserve && (
              <span style={{
                marginLeft: 8, fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: C.accent, verticalAlign: 'middle',
              }}>
                Recommended
              </span>
            )}
            <span style={{ display: 'block', fontWeight: 700, fontSize: '0.78rem', color: C.ink, marginTop: 2 }}>
              {qty(transition.normalizedPar, unit)}
            </span>
            <span style={{ display: 'block', fontWeight: 600, fontSize: '0.7rem', color: C.body }}>
              Equivalent to {round2(transition.newEquivalentPackages)} {plural(newLabel, transition.newEquivalentPackages)}
            </span>
          </button>
          <button
            type="button"
            disabled={busy}
            data-testid="par-transition-preserve-count"
            onClick={() => onPreservePackageCount?.()}
            style={{
              border: `1px solid ${C.border}`, background: C.panel, color: C.ink,
              borderRadius: 10, padding: '10px 12px', fontWeight: 700, fontSize: '0.85rem',
              cursor: busy ? 'wait' : 'pointer', textAlign: 'left',
            }}
          >
            Use the same number of {packageNoun(newLabel)}
            {suggestsPreserve && (
              <span style={{
                marginLeft: 8, fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: C.accent, verticalAlign: 'middle',
              }}>
                Suggested
              </span>
            )}
            <span style={{ display: 'block', fontWeight: 700, fontSize: '0.78rem', color: C.ink, marginTop: 2 }}>
              {transition.preservePackages} {plural(newLabel, transition.preservePackages)}
            </span>
            <span style={{ display: 'block', fontWeight: 600, fontSize: '0.7rem', color: C.body }}>
              {qty(transition.preserveNormalizedPar, unit)}
              {' — '}
              {qty(Math.abs(delta), unit)} {delta < 0 ? 'less' : 'more'} than the current PAR
            </span>
          </button>
          <button
            type="button"
            disabled={busy}
            data-testid="par-transition-later"
            onClick={() => onDismiss?.()}
            style={{
              border: 'none', background: 'none', color: C.muted,
              padding: '4px 0 0', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer',
            }}
          >
            Decide later
          </button>
        </div>
      </div>
    </div>
  );
}
